/*
 * @author: zhangning
 * @date: 2022/3/24 17:59
 * @Description: 密码登录组件
 **/
import React, { ReactElement, useEffect, useState } from 'react';
import { Button, Input, message } from 'antd';
import md5 from 'js-md5';
import { decryLogin } from '../../../../api/login/index.js';
import { useTranslation } from 'react-i18next';
import lookImg from '../../assets/look.png';
import noLookImg from '../../assets/nolook.png';
import { throttlFun, handleLocHref } from '../../../../utils/index.js';
import { encMsg, getPk } from '@/utils/kyberSm2';
import { useAppSelector } from '@/redux/hooks';
import type { ParamsType } from '../../types';
import { zhugeTrack } from '@/utils/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRequestParams } from '@/utils/index';
import './index.scss';

interface Props {
  loginSuccess: (params: any) => void;
}

const PwdLogin: React.FC<Props> = (props: any): ReactElement => {
  const { language } = useAppSelector((state) => state.common);
  const translation = useTranslation('login');
  const t = (params: string) => translation.t(params) || '';
  // 密码登录名
  const [name, setName] = useState<string>('');
  // 密码
  const [password, setPassword] = useState<string>('');
  // 图形验证码
  const [code, setCode] = useState<string>('');
  // 图形验证码 url
  const [codeUrl, setCodeUrl] = useState<string>('/uc/code/imageCode.json?username=');
  // 是否显示图形验证码
  const [showCode, setShowCode] = useState<boolean>(false);
  // 激活登录按钮
  const [btnActive, setBtnActive] = useState<boolean>(false);
  // 登录loading状态
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  // 是否显示密码
  const [showPwd, setShowPwd] = useState<boolean>(false);
  // 密码登录 name 错误提示
  const [nameErrText, setNameErrText] = useState<string>('');
  // 验证码错误提示
  const [codeErrText, setCodeErrText] = useState<string>('');
  // 验证码错误提示
  const [pwdErrText, setPwdErrText] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  // 账号密码登录
  const handleSubmit = async (): Promise<void> => {
    if (!btnActive || loginLoading) {
      return;
    }
    setLoginLoading(true);
    const loginparams: ParamsType = {
      username: name,
      password: md5(password),
      sign: 'qcloud_pc_tel_pwd_login_submit'
    };
    // 是否需要输入图形验证码
    loginparams.imageCode = showCode ? code || '' : '';
    // console.log('loginparams', loginparams);
    const { cipher1, cipher2, salt } = await getPk(true);
    const { cText3 }: any = await encMsg(JSON.stringify(loginparams), salt);
    const params = { login: cText3, cipher1, cipher2, salt };
    // login替换为decryLogin
    decryLogin(params)
      .then(async (result: { data: any }) => {
        const res = result.data;
        setCodeErrText('');
        setNameErrText('');
        setPwdErrText('');
        if (res.success) {
          props.loginSuccess(res.obj);
          if (!location.search) {
            window.open(handleLocHref('') + language, '_self');
          }
          zhugeTrack('用户登录', { '登录状态': '登录成功', '登录方式': '密码登录' });
        } else {
          setLoginLoading(false);
          zhugeTrack('用户登录', { '登录状态': '登录失败', '登录方式': '密码登录' });
          changeCode();
          if (res.code === 505) {
            setBtnActive(false);
            setShowCode(true);
            setCodeErrText(res.message);
          } else if (res.code === 10059 || res.code === 10058) {
            setBtnActive(false);
            setShowCode(true);
            setPwdErrText(res.message);
          } else if (res.code === 0 || res.code === 10038) {
            setPwdErrText(res.message);
          } else {
            message.error(res.message, 1);
          }
        }
      })
      .catch(() => {
        setLoginLoading(false);
      });
  };

  // 刷新密码登录图形验证码
  const changeCode = (): void => {
    setCodeUrl(`/uc/code/imageCode.json?username=${name}&time=` + new Date().getTime());
  };

  useEffect(() => {
    if (showCode) {
      setBtnActive(password !== '' && name !== '' && code.length === 4);
      return;
    }
    setBtnActive(password !== '' && name !== '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, password, code]);
  const handleGoReg = (): void => {
    const hrefUrl = getRequestParams('forward');
    if (hrefUrl) {
      navigate(`/${language}/l/register${location.search}`);
    } else {
      navigate(`/${language}/l/register`);
    }
  };

  const handleGoReset = (): void => {
    const hrefUrl = getRequestParams('forward');
    if (hrefUrl) {
      navigate(`/${language}/l/NTPASS${location.search}`);
    } else {
      navigate(`/${language}/l/NTPASS`);
    }
  };
  return (
    <div className="form-box-item">
      <div className="form-box-item-item">
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder={t('login_name_text')}
        />
        <span className="form-item-err-text">{nameErrText}</span>
      </div>
      <div className="form-box-item-item">
        <Input
          className={`${pwdErrText ? 'input-error' : ''}`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.keyCode === 13 && !showCode) {
              handleSubmit();
            }
          }}
          maxLength={32}
          type={`${showPwd ? 'text' : 'password'}`}
          placeholder={t('pwd')}
        />
        <img onClick={() => setShowPwd(!showPwd)} className="pwd-look-icon" src={showPwd ? lookImg : noLookImg} alt={t('view')} />
        <span className="form-item-err-text">{pwdErrText}</span>
      </div>
      {showCode && (
        <div className="form-box-item-item">
          <Input
            className={`${codeErrText ? 'input-error' : ''}`}
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                handleSubmit();
              }
            }}
            maxLength={4}
          />
          <img className="get-img-code" src={codeUrl} onClick={changeCode} alt={t('VerificationCode')} />
          <span className="form-item-err-text">{codeErrText}</span>
        </div>
      )}

      <div className="form-box-item-item">
        <Button onClick={() => throttlFun(handleSubmit)} className={`submit ${btnActive && !loginLoading ? 'active' : ''}`} disabled={loginLoading}>
          {loginLoading ? (language === 'en' ? 'Logging in...' : '登录中...') : t('new_loginText')}
        </Button>
      </div>
      <div className="form-box-item-item text pwd-bottom">
        <span onClick={handleGoReset}>{t('login_forgot_pwd')}</span>
        <div onClick={handleGoReg} className="register-pwd">
          {language === 'zh' && <span>还没有账号？</span>}
          {t('login_pwd_register')}
        </div>
      </div>
    </div>
  );
};

export default PwdLogin;
